import React, { useState, useEffect, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiX, FiRefreshCw } from 'react-icons/fi'
import api from '../../libs/axios'
import { useAuth } from '../../provider/auth.provider'
import toast from 'react-hot-toast'
import { useDebounce } from '../../hooks/useDebounce'

interface ActivityOption {
  id: number
  activity_name: string
}

interface VendorInvoiceItem {
  id: number
  item_name: string
  item_description?: string | null
  is_active: boolean
  created_at: string
  activities?: ActivityOption[]
}

interface Pagination {
  total_count: number
  page: number
  total_pages: number
  limit: number
  has_next_page: boolean
  has_prev_page: boolean
}

const EMPTY_FORM: { item_name: string; item_description: string; activity_ids: number[] } = {
  item_name: '', item_description: '', activity_ids: [],
}

const VendorInvoiceItemsPage: React.FC = () => {
  const { hasPermission } = useAuth()
  const canCreate = hasPermission('VendorInvoiceItem', 'create')
  const canUpdate = hasPermission('VendorInvoiceItem', 'update')
  const canDelete = hasPermission('VendorInvoiceItem', 'delete')

  const [items, setItems] = useState<VendorInvoiceItem[]>([])
  const [pagination, setPagination] = useState<Pagination>({ total_count: 0, page: 1, total_pages: 1, limit: 10, has_next_page: false, has_prev_page: false })
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editItem, setEditItem] = useState<VendorInvoiceItem | null>(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  // Activity master options for the "used in activities" multi-select
  const [activities, setActivities] = useState<ActivityOption[]>([])
  const [activitySearch, setActivitySearch] = useState('')

  const [deleteTarget, setDeleteTarget] = useState<VendorInvoiceItem | null>(null)
  const [deleting, setDeleting] = useState(false)

  const debouncedSearch = useDebounce(search, 400)

  const fetchItems = useCallback(async () => {
    setLoading(true)
    try {
      const params: any = { page, limit: 10 }
      if (debouncedSearch) params.search = debouncedSearch
      const res = await api.get('/vendor-invoice-items/list', { params })
      setItems(res.data.data?.items ?? [])
      setPagination(res.data.data?.pagination ?? pagination)
    } catch {
      toast.error('Failed to load vendor invoice items')
    } finally {
      setLoading(false)
    }
  }, [page, debouncedSearch])

  useEffect(() => { fetchItems() }, [fetchItems])
  useEffect(() => { setPage(1) }, [debouncedSearch])

  // Load the Activity master once for the multi-select
  useEffect(() => {
    api.get('/activities/active')
      .then(res => setActivities(res.data.data?.activities ?? []))
      .catch(() => { /* non-blocking */ })
  }, [])

  const openCreate = () => { setEditItem(null); setForm(EMPTY_FORM); setActivitySearch(''); setDrawerOpen(true) }
  const openEdit = (item: VendorInvoiceItem) => {
    setEditItem(item)
    setForm({
      item_name: item.item_name,
      item_description: item.item_description ?? '',
      activity_ids: (item.activities ?? []).map(a => a.id),
    })
    setActivitySearch('')
    setDrawerOpen(true)
  }
  const closeDrawer = () => { setDrawerOpen(false); setEditItem(null); setForm(EMPTY_FORM); setActivitySearch('') }

  const toggleActivity = (id: number) => setForm(f => ({
    ...f,
    activity_ids: f.activity_ids.includes(id) ? f.activity_ids.filter(x => x !== id) : [...f.activity_ids, id],
  }))

  const handleSave = async () => {
    if (!form.item_name.trim()) { toast.error('Item name is required'); return }
    setSaving(true)
    try {
      if (editItem) {
        await api.put(`/vendor-invoice-items/${editItem.id}`, form)
        toast.success('Item updated')
      } else {
        await api.post('/vendor-invoice-items/create', form)
        toast.success('Item created')
      }
      closeDrawer()
      fetchItems()
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save item')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleStatus = async (item: VendorInvoiceItem) => {
    try {
      await api.patch(`/vendor-invoice-items/${item.id}/status`, { is_active: !item.is_active })
      toast.success(`Item ${!item.is_active ? 'activated' : 'deactivated'}`)
      fetchItems()
    } catch {
      toast.error('Failed to update status')
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await api.delete(`/vendor-invoice-items/${deleteTarget.id}`)
      toast.success('Item deleted')
      setDeleteTarget(null)
      fetchItems()
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete item')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <Helmet><title>Vendor Invoice Items</title></Helmet>
      <div className="space-y-4">
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Vendor Invoice Items</h2>
              <p className="text-sm text-gray-500 mt-0.5">{pagination.total_count} items</p>
            </div>
            <div className="flex gap-2 items-center">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search items..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9 pr-8 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 w-56"
                />
                <FiSearch className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                {search && <button onClick={() => setSearch('')} className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600"><FiX className="w-4 h-4" /></button>}
              </div>
              <button onClick={() => fetchItems()} className="p-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-600"><FiRefreshCw className="w-4 h-4" /></button>
              {canCreate && (
                <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 text-sm font-medium">
                  <FiPlus className="w-4 h-4" /> Add Item
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Activities</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created At</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>{[...Array(7)].map((_, j) => <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>)}</tr>
                  ))
                ) : items.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400 text-sm">No items found</td></tr>
                ) : items.map((item, idx) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-500">{(page - 1) * 10 + idx + 1}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.item_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{item.item_description || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs">
                      {item.activities && item.activities.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {item.activities.map(a => (
                            <span key={a.id} className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs">
                              {a.activity_name}
                            </span>
                          ))}
                        </div>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {item.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{new Date(item.created_at).toLocaleDateString('en-IN')}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {canUpdate && (
                          <button onClick={() => openEdit(item)} className="p-1 text-blue-600 hover:text-blue-800" title="Edit"><FiEdit2 className="w-4 h-4" /></button>
                        )}
                        {canUpdate && (
                          <button onClick={() => handleToggleStatus(item)} className={`text-xs px-2 py-0.5 rounded border ${item.is_active ? 'border-orange-300 text-orange-600 hover:bg-orange-50' : 'border-green-300 text-green-600 hover:bg-green-50'}`}>
                            {item.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        )}
                        {canDelete && (
                          <button onClick={() => setDeleteTarget(item)} className="p-1 text-red-500 hover:text-red-700" title="Delete"><FiTrash2 className="w-4 h-4" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagination.total_count > 10 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm text-gray-500">
              <span>Showing {(page - 1) * 10 + 1}–{Math.min(page * 10, pagination.total_count)} of {pagination.total_count}</span>
              <div className="flex gap-2">
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border border-gray-200 rounded disabled:opacity-40 hover:bg-gray-50">Prev</button>
                <button disabled={!pagination.has_next_page} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border border-gray-200 rounded disabled:opacity-40 hover:bg-gray-50">Next</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Drawer */}
      {drawerOpen && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-40 z-40" onClick={closeDrawer} />
          <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col">
            <div className="flex items-center justify-between p-5 border-b bg-gray-50">
              <h3 className="text-base font-semibold text-gray-900">{editItem ? 'Edit Item' : 'Add Item'}</h3>
              <button onClick={closeDrawer} className="p-2 rounded hover:bg-gray-200"><FiX className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Item Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.item_name}
                  onChange={e => setForm(f => ({ ...f, item_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Survey Work, Report Preparation"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={form.item_description}
                  onChange={e => setForm(f => ({ ...f, item_description: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe this invoice item..."
                />
              </div>

              {/* Activities where this item can be used (item 4) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Used in Activities
                  {form.activity_ids.length > 0 && (
                    <span className="ml-1 text-xs text-gray-400">({form.activity_ids.length} selected)</span>
                  )}
                </label>
                <p className="text-xs text-gray-400 mb-2">Select the activities where this invoice item may be used.</p>
                <div className="relative mb-2">
                  <input
                    type="text"
                    placeholder="Search activities..."
                    value={activitySearch}
                    onChange={e => setActivitySearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                  />
                  <FiSearch className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                </div>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded divide-y divide-gray-100">
                  {activities.length === 0 ? (
                    <div className="px-3 py-4 text-center text-xs text-gray-400">No activities available</div>
                  ) : (
                    activities
                      .filter(a => a.activity_name.toLowerCase().includes(activitySearch.trim().toLowerCase()))
                      .map(a => (
                        <label key={a.id} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.activity_ids.includes(a.id)}
                            onChange={() => toggleActivity(a.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          {a.activity_name}
                        </label>
                      ))
                  )}
                </div>
              </div>
            </div>
            <div className="border-t bg-gray-50 px-5 py-4 flex justify-end gap-3">
              <button onClick={closeDrawer} disabled={saving} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button onClick={handleSave} disabled={saving || !form.item_name.trim()} className="px-4 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900 disabled:opacity-50">
                {saving ? 'Saving...' : editItem ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-40 z-40" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded shadow-xl max-w-sm w-full p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-2">Delete Item</h3>
              <p className="text-sm text-gray-600 mb-4">Are you sure you want to delete <strong>{deleteTarget.item_name}</strong>? This cannot be undone.</p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setDeleteTarget(null)} disabled={deleting} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">Cancel</button>
                <button onClick={handleDelete} disabled={deleting} className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50">
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default VendorInvoiceItemsPage
